import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import API, { getMediaURL } from '../../../../context/API';
import { requireActiveStoreId } from '../../../../utils/activeStore';
import type { AppDispatch, RootState } from '../../../../redux/store';
import { setCategoryItems as setCategoryItemsRedux } from '../../store/menuSlice';
import { AddToItemsProps, Category, CategoryItem, RelatedItem } from '../types';
import { API_ENDPOINTS, SUBMISSION_TYPES } from '../constants';

export const useAddToItems = (
  customizationData: AddToItemsProps['customizationData'],
  handleSubmit: AddToItemsProps['handleSubmit'],
  handleRelatedItemsChange: AddToItemsProps['handleRelatedItemsChange']
) => {
  const dispatch = useDispatch<AppDispatch>();
  const menuCategories = useSelector((state: RootState) => state.menu.categories);
  const categoryItemsMap = useSelector((state: RootState) => state.menu.categoryItemsMap);

  const [relatedItems, setRelatedItems] = useState<RelatedItem[]>(
    (customizationData.related_items || []).map((relatedItem) => {
      const itemValue = typeof relatedItem.item === 'string'
        ? relatedItem.item
        : (relatedItem.item as any)?.id
          ? { id: (relatedItem.item as any).id, item: (relatedItem.item as any).item, picture: (relatedItem.item as any).picture, price: (relatedItem.item as any).price }
          : relatedItem.item;

      return {
        id: typeof relatedItem.item === 'object' && relatedItem.item !== null && 'id' in relatedItem.item
          ? (relatedItem.item as any).id
          : relatedItem.id,
        item: itemValue,
        sequence_number: relatedItem.sequence_number || 0,
      };
    })
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(true);
  const [categories, setCategories] = useState<Category[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [categoryItems, setCategoryItems] = useState<CategoryItem[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingItems, setLoadingItems] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const response = await API.get(`${API_ENDPOINTS.CATEGORIES}?storeId=${requireActiveStoreId()}`);
        if (response.data.success) {
          const categoriesData = response.data.data.map((category: Category) => ({
            ...category,
            picture: getMediaURL(category.picture),
          }));
          setCategories(categoriesData);
          setFilteredCategories(categoriesData);
        }
      } catch {
        // Error fetching categories
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (menuCategories.length > 0) {
      setLoadingCategories(false);
      const categoriesData = menuCategories.map((category) => ({
        id: category.id,
        category: category.category,
        picture: category.picture,
      }));
      setCategories(categoriesData);
      setFilteredCategories(categoriesData);
    }
  }, [menuCategories]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const query = e.target.value.toLowerCase();
      setSearchQuery(query);
      setFilteredCategories(categories.filter((c) => c.category.toLowerCase().includes(query)));
    },
    [categories]
  );

  const handleSearchFocus = useCallback(() => {
    setIsSearching(true);
    setSelectedCategory(null);
  }, []);

  const handleCategoryClick = useCallback(
    async (categoryId: number) => {
      try {
        setLoadingItems(true);
        let itemsData: CategoryItem[] = [];

        const mapItem = (item: any): CategoryItem => ({
          id: item.id,
          item: item.item,
          picture: getMediaURL(item.picture),
          price: item.price,
          isChecked: relatedItems.some((ri) => {
            const itemId = typeof ri.item === 'object' && ri.item !== null && 'id' in ri.item
              ? (ri.item as any).id : null;
            return ri.id === item.id || itemId === item.id;
          }),
        });

        if (categoryItemsMap[categoryId]) {
          itemsData = categoryItemsMap[categoryId].map(mapItem);
        } else {
          const response = await API.get(
            `${API_ENDPOINTS.CATEGORY_ITEMS(categoryId)}?storeId=${requireActiveStoreId()}`
          );
          if (response.data.success) {
            itemsData = response.data.data.map(mapItem);
            (dispatch as any)(setCategoryItemsRedux({ categoryId, items: response.data.data }));
          }
        }

        setCategoryItems(itemsData);
        setSelectedCategory(categoryId);
      } catch {
        // Error fetching category items
      } finally {
        setLoadingItems(false);
      }
    },
    [relatedItems, categoryItemsMap, dispatch]
  );

  const handleRemoveItem = useCallback(
    (itemId: number) => {
      const updated = relatedItems.filter((item) => (item.item as any)?.id !== itemId);
      setRelatedItems(updated);
      handleRelatedItemsChange(updated);
    },
    [relatedItems, handleRelatedItemsChange]
  );

  const handleCheckboxChange = useCallback(
    (item: CategoryItem) => {
      const isSelected = relatedItems.some(
        (ri) => ri.id === item.id || (ri.item as any)?.id === item.id
      );

      const updatedRelatedItems = isSelected
        ? relatedItems.filter((ri) => ri.id !== item.id && (ri.item as any)?.id !== item.id)
        : [...relatedItems, { id: item.id, item, sequence_number: relatedItems.length }];

      setCategoryItems((prev) =>
        prev.map((ci) => ci.id === item.id ? { ...ci, isChecked: !isSelected } : ci)
      );
      setRelatedItems(updatedRelatedItems);
      handleRelatedItemsChange(updatedRelatedItems);
    },
    [relatedItems, handleRelatedItemsChange]
  );

  const saveChanges = useCallback(async () => {
    setIsSaving(true);
    try {
      await handleSubmit(SUBMISSION_TYPES.ADD_TO_ITEMS, {
        ...customizationData,
        related_items: relatedItems,
      });
    } finally {
      setIsSaving(false);
    }
  }, [customizationData, relatedItems, handleSubmit]);

  const clearCategory = useCallback(() => {
    setSelectedCategory(null);
    setCategoryItems([]);
  }, []);

  return {
    relatedItems,
    searchQuery,
    isSearching,
    filteredCategories,
    selectedCategory,
    categoryItems,
    loadingCategories,
    loadingItems,
    isSaving,
    handleSearchChange,
    handleSearchFocus,
    handleCategoryClick,
    handleRemoveItem,
    handleCheckboxChange,
    saveChanges,
    clearCategory,
  };
};
